import { useColorMode } from "@/ThemeProvider";
import {
    Add,
    Check,
    CheckBoxOutlined,
    Checklist,
    Close,
    DeleteOutline,
    Done,
    Download as DownloadIcon,
    ExpandMore,
    GridView,
    Reorder as ReorderIcon,
    Search,
} from "@mui/icons-material";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import HelpIcon from "@mui/icons-material/Help";
import {
    AppBar,
    Alert,
    Backdrop,
    Box,
    Button,
    ButtonBase,
    CircularProgress,
    Container,
    Divider,
    Grid2 as Grid,
    IconButton,
    InputBase,
    Menu,
    MenuItem,
    Paper,
    Toolbar,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ProjectCard from "./ProjectCard";
import ProjectListView from "./ProjectListView";
import UserProfile from "./UserProfile";
import mdvLogo from "./assets/mdv_logo.png";
import useProjects from "./hooks/useProjects";
import {
    type SortBy,
    type SortOrder,
    sortProjects,
} from "./utils/projectUtils";
import AlertErrorComponent from "@/charts/dialogs/AlertErrorComponent";
import ImportProjectDialog from "@/react/components/ImportProjectDialog";
import usePermissions from "./PermissionsContext";
import useAuthEnabled from "./hooks/useAuthEnabled";
import { RefreshCwIcon } from "lucide-react";
import ReusableAlertDialog from "@/charts/dialogs/ReusableAlertDialog";
import HelpDialog from "./HelpDialog";
import { buildProjectUrl, shouldShowLocalBackendNotice } from "@/utils/mdvRouting";
import BulkDeleteProjectsDialog from "./components/BulkDeleteProjectsDialog";
import DashboardActionButton from "./components/DashboardActionButton";
import ExtensionNavigation from "./components/ExtensionNavigation";
import RecycleBinDialog from "./components/RecycleBinDialog";

// todo: Refactor the code into different components and hooks for cleaner and readable code
// Maybe use a design pattern? As displaying certain components depend on some states
const Dashboard: React.FC = () => {
    const isProduction = import.meta.env.PROD;
    const {
        projects,
        isLoading: projectsLoading,
        error,
        rescanWarning,
        isErrorModalOpen,
        closeErrorModal,
        clearRescanWarning,
        fetchProjects,
        createProject,
        deleteProject,
        softDeleteProjects,
        fetchRecycleBin,
        purgeRecycleBin,
        restoreRecycledProject,
        renameProject,
        changeProjectType,
        setFilter,
        exportProject,
        rescanProjects,
    } = useProjects();
    const { permissions, isLoading: permissionsLoading, isPublicPage } = usePermissions();

    // Check if auth is enabled
    const authEnabled = useAuthEnabled();
    const { mode, toggleColorMode } = useColorMode();
    const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
    const [sortBy, setSortBy] = useState<SortBy>("lastModified");
    const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [open, setOpen] = useState(false);
    const [helpDialogOpen, setHelpDialogOpen] = useState(false);
    const [customLogoVisible, setCustomLogoVisible] = useState(true);
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set());
    const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
    const [isRecycleBinOpen, setIsRecycleBinOpen] = useState(false);
    const theme = useTheme();
    const showLocalBackendNotice = shouldShowLocalBackendNotice();

    const isLoading = projectsLoading || permissionsLoading;

    React.useEffect(() => {
        fetchProjects();
    }, [fetchProjects]);

    useEffect(() => {
        if (!permissionsLoading && isPublicPage) {
            setSortBy("name");
            setSortOrder("asc");
        }
    }, [isPublicPage, permissionsLoading]);

    const handleCreateProject = async () => {
        try {
            const newProject = await createProject();
            if (!newProject?.id) {
                console.error("Project creation succeeded without a valid project id.", newProject);
                return;
            }

            window.location.href = buildProjectUrl(newProject.id);
        } catch (error) {
            console.error("Failed to create project:", error);
        }
    };

    const sortedProjects = useMemo(() => {
        return sortProjects(projects, sortBy, sortOrder);
    }, [projects, sortBy, sortOrder]);

    const handleSort = useCallback(
        (newSortBy: SortBy) => {
            if (newSortBy === sortBy) {
                // Toggle sort order if clicking the same sort option
                setSortOrder(sortOrder === "asc" ? "desc" : "asc");
            } else {
                // Set new sort type with default desc order for both cases
                setSortBy(newSortBy);
                setSortOrder("desc");
            }
            setAnchorEl(null);
        },
        [sortBy, sortOrder],
    );

    const toggleDropdown = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const onRescanClick = useCallback(
        async () => {
            await rescanProjects();
        }, [rescanProjects]
    );

    const handleSelectionChange = useCallback((id: string, selected: boolean) => {
        setSelectedProjectIds((currentIds) => {
            const nextIds = new Set(currentIds);
            if (selected) {
                nextIds.add(id);
            } else {
                nextIds.delete(id);
            }
            return nextIds;
        });
    }, []);

    const handleBulkDelete = async () => {
        try {
            await softDeleteProjects([...selectedProjectIds]);
            setSelectedProjectIds(new Set());
            setSelectionMode(false);
            setIsBulkDeleteOpen(false);
        } catch {
            // The hook opens the shared error dialog.
        }
    };

    const exitSelectionMode = useCallback(() => {
        setSelectionMode(false);
        setSelectedProjectIds(new Set());
    }, []);

    return (
        <>
            <Box
                sx={{
                    flexGrow: 1,
                    bgcolor: "background.default",
                    minHeight: "100vh",
                }}
            >
                <AppBar position="static" color="default" elevation={0}>
                    <Toolbar>
                        <Box
                            component="img"
                            sx={{
                                height: 40,
                                mr: 2,
                            }}
                            alt="MDV Projects Logo"
                            src={mdvLogo}
                        />
                        <Box
                            component="img"
                            sx={{
                                height: 40,
                                ml: 1,
                                display: customLogoVisible ? "block" : "none",
                            }}
                            alt="Custom deployment logo"
                            src="secondary_logo"
                            onError={() => setCustomLogoVisible(false)}
                        />
                        <ExtensionNavigation />
                        <Box sx={{ flexGrow: 1 }} />
                        <Paper
                            component="form"
                            sx={{
                                p: "2px 4px",
                                display: "flex",
                                alignItems: "center",
                                width: 400,
                                mr: 2,
                            }}
                        >
                            <InputBase
                                sx={{ ml: 1, flex: 1 }}
                                placeholder="Search projects"
                                inputProps={{ "aria-label": "search projects" }}
                                onChange={(e) => setFilter(e.target.value)}
                            />
                            <IconButton
                                type="submit"
                                sx={{ p: "10px" }}
                                aria-label="search"
                            >
                                <Search />
                            </IconButton>
                        </Paper>
                        {authEnabled && <UserProfile />}
                        <Tooltip title="Toggle theme">
                            <IconButton
                                sx={{ mr: 2 }}
                                onClick={toggleColorMode}
                                color="inherit"
                                data-testid="theme_toggle_catalog"
                                aria-label="Toggle theme"
                            >
                                {mode === "dark" ? (
                                    <Brightness4Icon />
                                ) : (
                                    <Brightness7Icon />
                                )}
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            startIcon={<HelpIcon />}
                            onClick={() => setHelpDialogOpen(true)}
                            sx={{
                                borderWidth: 1.5,
                                borderRadius: 2,
                                textTransform: "none",
                                px: 1.5,
                                py: 0.75,
                            }}
                        >
                            Help / Feedback
                        </Button>
                    </Toolbar>
                </AppBar>

                <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                    {showLocalBackendNotice && (
                        <Alert severity="info" sx={{ mb: 3 }}>
                            This preview build needs to talk to an MDV app running locally on <code>http://localhost:5055</code>.
                            If your browser asks whether this site can communicate with local apps or devices, allow it for this preview:
                            the Netlify page is only the frontend, and project lists plus <code>/project/:id</code> data are loaded from your local MDV container.
                            No local data is sent to a remote server.
                        </Alert>
                    )}
                    {rescanWarning && (
                        <Alert
                            severity="warning"
                            onClose={clearRescanWarning}
                            sx={{ mb: 3 }}
                        >
                            {rescanWarning}
                        </Alert>
                    )}
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        {!isProduction && permissions.createProject && (
                            <Grid size={{xs: 12, sm: 6, md: 4, lg: 3}}>
                                <ButtonBase
                                    sx={{
                                        width: "100%",
                                        display: "block",
                                        textAlign: "center",
                                    }}
                                >
                                    <Paper
                                        sx={{
                                            p: 2,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                        }}
                                        onClick={() => handleCreateProject()}
                                    >
                                        <Add
                                            sx={{
                                                fontSize: 40,
                                                color: "primary.main",
                                                mb: 1,
                                            }}
                                        />
                                        <Typography variant="subtitle1" align="center">
                                            Create new project
                                        </Typography>
                                    </Paper>
                                </ButtonBase>
                            </Grid>
                        )}
                        {!isProduction && permissions.importProject && (
                            <Grid size={{xs: 12, sm: 6, md: 4, lg: 3}}>
                                <ButtonBase
                                    sx={{
                                        width: "100%",
                                        display: "block",
                                        textAlign: "center",
                                    }}
                                >
                                    <Paper
                                        sx={{
                                            p: 2,
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                        }}
                                        onClick={() => setOpen(true)}
                                    >
                                        <DownloadIcon
                                            sx={{
                                                fontSize: 40,
                                                color: "primary.main",
                                                mb: 1,
                                            }}
                                        />
                                        <Typography variant="subtitle1" align="center" sx={{}}>
                                            Import an existing project
                                        </Typography>
                                    </Paper>
                                </ButtonBase>
                            </Grid>
                        )}
                    </Grid>

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                        }}
                    >
                        <Typography variant="h5">{!isPublicPage ? "Recent Projects" : "Published Projects"}</Typography>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                            {!isPublicPage && permissions.deleteProject && (
                                <>
                                    <DashboardActionButton
                                        active={selectionMode}
                                        icon={selectionMode ? <Done /> : <Checklist />}
                                        label={selectionMode ? "Done" : "Select"}
                                        onClick={() =>
                                            selectionMode ? exitSelectionMode() : setSelectionMode(true)
                                        }
                                    />
                                    <DashboardActionButton
                                        icon={<DeleteOutline />}
                                        label="Recycle bin"
                                        onClick={() => setIsRecycleBinOpen(true)}
                                    />
                                </>
                            )}
                            {/* Hide rescan projects on public page */}
                            {!isPublicPage && (
                                <DashboardActionButton
                                    icon={<RefreshCwIcon />}
                                    label="Rescan Projects"
                                    onClick={onRescanClick}
                                />
                            )}

                            <Paper
                                elevation={1}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "4px",
                                    width: "205px",
                                    height: "50px",
                                    bgcolor: "background.paper",
                                }}
                            >
                                <Button
                                    endIcon={<ExpandMore />}
                                    onClick={toggleDropdown}
                                    sx={{
                                        padding: 1,
                                        textTransform: "none",
                                        width: "100%",
                                        height: "100%",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        {/* Hide last modified on public page */}
                                        Sort by:{" "}
                                        {!isPublicPage ?
                                            sortBy === "lastModified"
                                                ? `Last modified (${sortOrder === "desc" ? "Newest first" : "Oldest first"})`
                                                : `Name (${sortOrder === "desc" ? "Z to A" : "A to Z"})`
                                            :
                                            `Name (${sortOrder === "desc" ? "Z to A" : "A to Z"})`
                                        }
                                    </Box>
                                </Button>
                            </Paper>
                            <Tooltip
                                title={
                                    viewMode === "grid" ? "List View" : "Grid View"
                                }
                            >
                                <IconButton
                                    onClick={() =>
                                        setViewMode(
                                            viewMode === "grid" ? "list" : "grid",
                                        )
                                    }
                                    sx={{ ml: 2 }}
                                >
                                    {viewMode === "grid" ? (
                                        <ReorderIcon sx={{ fontSize: 32 }} />
                                    ) : (
                                        <GridView sx={{ fontSize: 32 }} />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={() => setAnchorEl(null)}
                        >
                            {!isPublicPage &&
                                (<MenuItem
                                    onClick={() => handleSort("lastModified")}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        width: "200px",
                                        gap: 1,
                                    }}
                                >
                                    <span>Last modified</span>
                                    {sortBy === "lastModified" && (
                                        <span>
                                            {sortOrder === "desc"
                                                ? "(Oldest first)"
                                                : "(Newest first)"
                                            }
                                        </span>
                                    )}
                                </MenuItem>)
                            }
                            <MenuItem
                                onClick={() => handleSort("name")}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    width: "200px",
                                    gap: 1,
                                }}
                            >
                                <span>Name</span>
                                {sortBy === "name" && (
                                    <span>
                                        {sortOrder === "desc" ? "A to Z" : "Z to A"}
                                    </span>
                                )}
                            </MenuItem>
                        </Menu>
                    </Box>

                    {selectionMode && selectedProjectIds.size > 0 && (
                        <Paper
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                p: 1.5,
                                mb: 2,
                            }}
                        >
                            <Typography color="text.secondary">
                                {selectedProjectIds.size} selected
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Button onClick={() => setSelectedProjectIds(new Set())}>
                                    Clear selection
                                </Button>
                                <Button
                                    color="error"
                                    variant="contained"
                                    onClick={() => setIsBulkDeleteOpen(true)}
                                >
                                    Delete selected
                                </Button>
                            </Box>
                        </Paper>
                    )}

                    <Divider sx={{ mb: 2 }} />

                    {viewMode === "grid" ? (
                        <Grid container spacing={4}>
                            {sortedProjects.map((project) => (
                                <Grid
                                    key={project.id}
                                    size={{
                                        xs: 12,
                                        sm: 6,
                                        md: 4,
                                        lg: 3
                                    }}
                                >
                                    <ProjectCard
                                        {...project}
                                        onDelete={deleteProject}
                                        onRename={renameProject}
                                        onChangeType={changeProjectType}
                                        onAddCollaborator={(email) => {}}
                                        onExport={exportProject}
                                        selectionMode={selectionMode}
                                        selected={selectedProjectIds.has(project.id)}
                                        onSelectionChange={handleSelectionChange}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <ProjectListView
                            projects={sortedProjects}
                            onDelete={deleteProject}
                            onRename={renameProject}
                            onChangeType={changeProjectType}
                            onExport={exportProject}
                            selectionMode={selectionMode}
                            selectedProjectIds={selectedProjectIds}
                            onSelectionChange={handleSelectionChange}
                        />
                    )}
                </Container>
                {error && (
                    <ReusableAlertDialog
                        open={isErrorModalOpen}
                        handleClose={closeErrorModal}
                        isAlertErrorComponent
                        component={
                            <AlertErrorComponent
                                message={error as string}
                            />
                        }
                    />
                )}
                {open && !isProduction && permissions.importProject && (
                    <ImportProjectDialog open={open} setOpen={setOpen} />
                )}
                {helpDialogOpen && (
                    <HelpDialog
                        open={helpDialogOpen}
                        onClose={() => setHelpDialogOpen(false)}
                    />
                )}
                <BulkDeleteProjectsDialog
                    open={isBulkDeleteOpen}
                    selectedCount={selectedProjectIds.size}
                    onClose={() => setIsBulkDeleteOpen(false)}
                    onConfirm={handleBulkDelete}
                />
                <RecycleBinDialog
                    open={isRecycleBinOpen}
                    onClose={() => setIsRecycleBinOpen(false)}
                    fetchProjects={fetchRecycleBin}
                    onDeleteAll={purgeRecycleBin}
                    onRestore={restoreRecycledProject}
                />
            </Box>
            {isLoading && (
                <Backdrop
                    open={isLoading}
                    sx={{ zIndex: theme.zIndex.modal + 1 }}
                >
                    <CircularProgress color="inherit" />
                </Backdrop>
            )}
        </>
    );
};

export default Dashboard;
